import {
    ActionList,
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    ChoiceList,
    IndexFilters,
    IndexFiltersMode,
    IndexTable,
    InlineStack,
    Layout,
    Modal,
    Page,
    Popover,
    Text,
    useIndexResourceState,
    useSetIndexFiltersMode,
} from '@shopify/polaris';
import type { IndexFiltersProps, TabProps } from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    AtdStatusCount,
    AtdShipToHomeOrderListItem,
    AtdShipToHomeOrderListResponse,
} from '../../app/models/Atd';
import AtdTabs from './AtdTabs';

const pageSize = 25;

const statusFilterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Needs attention', value: 'NeedsAttention' },
    { label: 'In progress', value: 'InProgress' },
    { label: 'Submitted', value: 'Submitted' },
    { label: 'Shipped / fulfilled', value: 'ShippedFulfilled' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Archived', value: 'Archived' },
];

const sortOptions: IndexFiltersProps['sortOptions'] = [
    { label: 'Created', value: 'created desc', directionLabel: 'Newest first' },
    { label: 'Created', value: 'created asc', directionLabel: 'Oldest first' },
    { label: 'Updated', value: 'updated desc', directionLabel: 'Newest first' },
    { label: 'Updated', value: 'updated asc', directionLabel: 'Oldest first' },
];

const formatDateTime = (value?: string) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-';

const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') return JSON.stringify(data);
    return error?.message || fallback;
};

const orderCellStyle: React.CSSProperties = {
    textAlign: 'left',
    minWidth: 150,
};

const orderLinkButtonStyle: React.CSSProperties = {
    appearance: 'none',
    background: 'none',
    border: 0,
    color: 'inherit',
    cursor: 'pointer',
    font: 'inherit',
    margin: 0,
    padding: 0,
    textAlign: 'left',
};

const statusBadgeButtonStyle: React.CSSProperties = {
    appearance: 'none',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    margin: 0,
    padding: 0,
    textAlign: 'left',
};

const statusBadgeWrapStyle: React.CSSProperties = {
    display: 'inline-flex',
    width: 'fit-content',
};

const adjustStatusCounts = (counts: AtdStatusCount[], status: string | undefined, delta: number) => {
    if (!status) return counts;

    let found = false;
    const adjusted = counts
        .map(item => {
            if ((item.status || '').toLowerCase() !== status.toLowerCase()) {
                return item;
            }

            found = true;
            return { ...item, count: Math.max(0, item.count + delta) };
        })
        .filter(item => item.count > 0);

    if (!found && delta > 0) {
        adjusted.push({ status, count: delta });
    }

    return adjusted.sort((left, right) => (left.status || '').localeCompare(right.status || ''));
};

const normalizeToken = (value?: string) => (value || '').trim().toLowerCase();
const isShipToStoreOrder = (order: AtdShipToHomeOrderListItem) => normalizeToken(order.fulfillmentType) === 'shiptostore';
const getFulfillmentTypeLabel = (order: AtdShipToHomeOrderListItem) => isShipToStoreOrder(order) ? 'Ship to store' : 'Ship to home';
const toApiSort = (value: string) => value.replace(' ', '_');
type AtdOrderSelectionResource = AtdShipToHomeOrderListItem & Record<string, unknown>;

const normalizeStatusFromSearch = (search: string) => {
    const requestedStatus = new URLSearchParams(search).get('status') || 'all';
    return statusFilterOptions.some(option => option.value === requestedStatus)
        ? requestedStatus
        : 'all';
};

const AtdPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [result, setResult] = useState<AtdShipToHomeOrderListResponse | undefined>();
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState(() => normalizeStatusFromSearch(location.search));
    const [sort, setSort] = useState('created desc');
    const [query, setQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
    const [selectedTab, setSelectedTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [retryingId, setRetryingId] = useState<number | undefined>();
    const [syncingId, setSyncingId] = useState<number | undefined>();
    const [archivingId, setArchivingId] = useState<number | undefined>();
    const [unarchivingId, setUnarchivingId] = useState<number | undefined>();
    const [openActionOrderId, setOpenActionOrderId] = useState<number | undefined>();
    const [openErrorOrderId, setOpenErrorOrderId] = useState<number | undefined>();
    const [refreshPreviewOrder, setRefreshPreviewOrder] = useState<AtdShipToHomeOrderListItem | undefined>();
    const [bulkArchiveModalOpen, setBulkArchiveModalOpen] = useState(false);
    const [bulkArchiving, setBulkArchiving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [notice, setNotice] = useState<string | undefined>();

    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError(undefined);
            setResult(await agent.Atd.shipToHomeOrders(page, pageSize, status, submittedQuery, toApiSort(sort)));
        } catch (loadError: any) {
            setError(getErrorMessage(loadError, 'Unable to load ATD orders.'));
        } finally {
            setLoading(false);
        }
    }, [page, sort, status, submittedQuery]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    useEffect(() => {
        setPage(1);
        setStatus(normalizeStatusFromSearch(location.search));
    }, [location.search]);

    const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

    const openOrder = useCallback((order: AtdShipToHomeOrderListItem) => {
        navigate(`/integrations/atd/orders/${order.id}`);
    }, [navigate]);

    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setPage(1);
            setSubmittedQuery(query.trim());
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [query]);

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value);
    }, []);

    const handleQueryClear = useCallback(() => {
        setQuery('');
        setSubmittedQuery('');
        setPage(1);
    }, []);

    const handleStatusValueChange = useCallback((value: string) => {
        setPage(1);
        setStatus(value);
    }, []);

    const handleStatusChoiceChange = useCallback((value: string[]) => {
        handleStatusValueChange(value[0] || 'all');
    }, [handleStatusValueChange]);

    const handleStatusRemove = useCallback(() => {
        handleStatusValueChange('all');
    }, [handleStatusValueChange]);

    const handleSortChange = useCallback((value: string) => {
        setPage(1);
        setSort(value);
    }, []);

    const handleSortSelectedChange = useCallback((value: string[]) => {
        handleSortChange(value[0] || 'created desc');
    }, [handleSortChange]);

    const handleFiltersClearAll = useCallback(() => {
        setPage(1);
        setStatus('all');
        setQuery('');
        setSubmittedQuery('');
    }, []);

    const closeOrderActions = useCallback(() => {
        setOpenActionOrderId(undefined);
    }, []);

    const closeErrorDetails = useCallback(() => {
        setOpenErrorOrderId(undefined);
    }, []);

    const handleRetry = useCallback((order: AtdShipToHomeOrderListItem) => {
        // Keep confirmations inside the embedded app so the merchant sees a
        // consistent Shopify/Polaris experience.
        setRefreshPreviewOrder(order);
    }, []);

    const executeRetry = useCallback(async () => {
        if (!refreshPreviewOrder) return;

        try {
            setRetryingId(refreshPreviewOrder.id);
            setError(undefined);
            setNotice(undefined);
            await agent.Atd.retryShipToHomeOrder(refreshPreviewOrder.id);
            setNotice(`Refreshed preview for ${refreshPreviewOrder.shopifyOrderName || `ATD order ${refreshPreviewOrder.id}`}.`);
            await loadOrders();
        } catch (retryError: any) {
            setError(getErrorMessage(retryError, 'Unable to refresh ATD preview.'));
            await loadOrders();
        } finally {
            setRetryingId(undefined);
            setRefreshPreviewOrder(undefined);
        }
    }, [loadOrders, refreshPreviewOrder]);

    const closeRefreshPreviewModal = useCallback(() => {
        if (retryingId === undefined) {
            setRefreshPreviewOrder(undefined);
        }
    }, [retryingId]);

    const handleSyncOrderStatus = useCallback(async (order: AtdShipToHomeOrderListItem) => {
        try {
            setSyncingId(order.id);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.syncShipToHomeOrderStatus(order.id);
            setNotice(`Refreshed fulfillment status for ${detail.shopifyOrderName || `ATD order ${detail.id}`}.`);
            await loadOrders();
        } catch (syncError: any) {
            setError(getErrorMessage(syncError, 'Unable to refresh ATD fulfillment status.'));
            await loadOrders();
        } finally {
            setSyncingId(undefined);
        }
    }, [loadOrders]);

    const removeRowAfterArchiveChange = useCallback((order: AtdShipToHomeOrderListItem, archived: boolean) => {
        // Archive/restore is a row-level visibility change. Update the current
        // page state directly so the merchant does not see the whole list reload.
        setResult(current => {
            if (!current) return current;

            const rowWasVisible = current.items.some(item => item.id === order.id);
            return {
                ...current,
                totalCount: rowWasVisible ? Math.max(0, current.totalCount - 1) : current.totalCount,
                archivedCount: Math.max(0, current.archivedCount + (archived ? 1 : -1)),
                statusCounts: adjustStatusCounts(current.statusCounts, order.status, archived ? -1 : 1),
                items: current.items.filter(item => item.id !== order.id),
            };
        });
    }, []);

    const handleArchive = useCallback(async (order: AtdShipToHomeOrderListItem) => {
        try {
            setArchivingId(order.id);
            setError(undefined);
            setNotice(undefined);
            await agent.Atd.archiveShipToHomeOrder(order.id);
            removeRowAfterArchiveChange(order, true);
            setNotice(`Archived ${order.shopifyOrderName || `ATD order ${order.id}`}.`);
        } catch (archiveError: any) {
            setError(getErrorMessage(archiveError, 'Unable to archive ATD order.'));
            await loadOrders();
        } finally {
            setArchivingId(undefined);
        }
    }, [loadOrders, removeRowAfterArchiveChange]);

    const handleUnarchive = useCallback(async (order: AtdShipToHomeOrderListItem) => {
        try {
            setUnarchivingId(order.id);
            setError(undefined);
            setNotice(undefined);
            await agent.Atd.unarchiveShipToHomeOrder(order.id);
            removeRowAfterArchiveChange(order, false);
            setNotice(`Restored ${order.shopifyOrderName || `ATD order ${order.id}`} to the active ATD list.`);
        } catch (archiveError: any) {
            setError(getErrorMessage(archiveError, 'Unable to restore ATD order.'));
            await loadOrders();
        } finally {
            setUnarchivingId(undefined);
        }
    }, [loadOrders, removeRowAfterArchiveChange]);

    const tableRows = useMemo(() => result?.items ?? [], [result?.items]);
    const selectionRows = tableRows as AtdOrderSelectionResource[];
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState<AtdOrderSelectionResource>(selectionRows, {
            resourceIDResolver: (order) => String(order.id),
        });
    const selectedOrders = useMemo(() => (
        allResourcesSelected
            ? tableRows
            : tableRows.filter(order => selectedResources.includes(String(order.id)))
    ), [allResourcesSelected, selectedResources, tableRows]);
    const archiveCandidates = useMemo(() => (
        selectedOrders.filter(order => !order.archivedAt && order.canArchive)
    ), [selectedOrders]);
    const selectedCount = allResourcesSelected ? tableRows.length : selectedResources.length;
    const rowActionInProgress =
        retryingId !== undefined ||
        syncingId !== undefined ||
        archivingId !== undefined ||
        unarchivingId !== undefined ||
        bulkArchiving;

    const handleBulkArchive = useCallback(async () => {
        if (archiveCandidates.length === 0) {
            return;
        }

        try {
            setBulkArchiving(true);
            setError(undefined);
            setNotice(undefined);

            const failures: string[] = [];

            // Use the existing single-order archive endpoint so the bulk action
            // stays consistent with row-level archive behavior and permissions.
            for (const order of archiveCandidates) {
                try {
                    setArchivingId(order.id);
                    await agent.Atd.archiveShipToHomeOrder(order.id);
                } catch (archiveError: any) {
                    failures.push(`${order.shopifyOrderName || `ATD order ${order.id}`}: ${getErrorMessage(archiveError, 'Unable to archive')}`);
                }
            }

            const archivedCount = archiveCandidates.length - failures.length;
            setBulkArchiveModalOpen(false);
            clearSelection();
            await loadOrders();

            if (failures.length > 0) {
                setError(`Archived ${archivedCount} of ${archiveCandidates.length} selected ATD orders. ${failures.join('; ')}`);
            } else {
                setNotice(`Archived ${archivedCount} selected ATD order${archivedCount === 1 ? '' : 's'}.`);
            }
        } finally {
            setArchivingId(undefined);
            setBulkArchiving(false);
        }
    }, [archiveCandidates, clearSelection, loadOrders]);

    const renderStatusBadge = useCallback((order: AtdShipToHomeOrderListItem) => {
        const normalizedStep = normalizeToken(order.lifecycleStep);
        const normalizedStatus = normalizeToken(order.status);
        const hasCurrentError = normalizeToken(order.lifecycleState) === 'error' || !!order.errorMessage;
        const isReadyForPickup = normalizedStatus === 'readyforpickup' || normalizedStep === 'ready for pickup';
        const isFulfilled = normalizedStatus === 'fulfilled' || !!order.shopifyFulfilledAt;
        const isShipmentInProgress = normalizedStep === 'shipment in progress';
        const isStoreDelivery = normalizedStep === 'store delivery' || normalizedStatus === 'waitingforstoredelivery';
        const isPreviewOnly = normalizedStep === 'order previewed';
        const fallbackLabel = order.lifecycleStep || order.status || 'Unknown';

        let badge = <Badge>{fallbackLabel}</Badge>;

        if (hasCurrentError) {
            badge = <Badge tone="critical">{order.errorMessage ? 'Action required - with errors' : 'Action required'}</Badge>;
        } else if (isReadyForPickup) {
            badge = <Badge tone="success" progress="complete">Ready for pickup</Badge>;
        } else if (isFulfilled) {
            badge = <Badge progress="complete">Fulfilled</Badge>;
        } else if (isShipmentInProgress || isStoreDelivery) {
            badge = <Badge tone="info">{fallbackLabel}</Badge>;
        } else if (isPreviewOnly) {
            badge = <Badge progress="incomplete">{fallbackLabel}</Badge>;
        }

        if (!order.errorMessage) {
            return <span style={statusBadgeWrapStyle}>{badge}</span>;
        }

        return (
            <span style={statusBadgeWrapStyle}>
                <Popover
                    active={openErrorOrderId === order.id}
                    activator={
                        <button
                            type="button"
                            style={statusBadgeButtonStyle}
                            onClick={() => setOpenErrorOrderId(current => current === order.id ? undefined : order.id)}
                        >
                            {badge}
                        </button>
                    }
                    onClose={closeErrorDetails}
                >
                    <Box padding="300">
                        <div style={{ maxWidth: 420, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                            <BlockStack gap="100">
                                <Text as="p" fontWeight="semibold">Current error</Text>
                                <Text as="p" tone="critical">{order.errorMessage}</Text>
                            </BlockStack>
                        </div>
                    </Box>
                </Popover>
            </span>
        );
    }, [closeErrorDetails, openErrorOrderId]);

    const selectedStatusOption = statusFilterOptions.find(option => option.value === status);
    const filters: IndexFiltersProps['filters'] = [
        {
            key: 'status',
            label: 'Status',
            filter: (
                <ChoiceList
                    title="Status"
                    titleHidden
                    choices={statusFilterOptions}
                    selected={[status]}
                    onChange={handleStatusChoiceChange}
                />
            ),
            shortcut: true,
        },
    ];
    const appliedFilters: IndexFiltersProps['appliedFilters'] = [];

    if (status !== 'all') {
        appliedFilters.push({
            key: 'status',
            label: `Status: ${selectedStatusOption?.label || status}`,
            onRemove: handleStatusRemove,
        });
    }

    const promotedBulkActions = [
        {
            content: 'Archive orders',
            destructive: true,
            onAction: () => setBulkArchiveModalOpen(true),
            disabled: rowActionInProgress || archiveCandidates.length === 0,
        },
    ];

    return (
        <>
            <Modal
                open={Boolean(refreshPreviewOrder)}
                onClose={closeRefreshPreviewModal}
                title="Refresh ATD preview"
                primaryAction={{
                    content: 'Refresh preview',
                    onAction: executeRetry,
                    loading: retryingId === refreshPreviewOrder?.id,
                    disabled: retryingId !== undefined,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: closeRefreshPreviewModal,
                    disabled: retryingId !== undefined,
                }]}
            >
                <Modal.Section>
                    <Text as="p">Refresh this ATD preview from the latest Shopify fulfillment data?</Text>
                </Modal.Section>
            </Modal>

            <Modal
                open={bulkArchiveModalOpen}
                onClose={() => {
                    if (!bulkArchiving) {
                        setBulkArchiveModalOpen(false);
                    }
                }}
                title="Archive selected ATD orders"
                primaryAction={{
                    content: `Archive ${archiveCandidates.length} order${archiveCandidates.length === 1 ? '' : 's'}`,
                    destructive: true,
                    onAction: handleBulkArchive,
                    loading: bulkArchiving,
                    disabled: bulkArchiving || archiveCandidates.length === 0,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setBulkArchiveModalOpen(false),
                    disabled: bulkArchiving,
                }]}
            >
                <Modal.Section>
                    <Text as="p">
                        Archive {archiveCandidates.length} of {selectedCount} selected ATD order{selectedCount === 1 ? '' : 's'} from the active list?
                    </Text>
                </Modal.Section>
            </Modal>

            <Page
                title="ATD orders"
                backAction={{ content: 'Integrations', onAction: () => navigate('/settings/integrations') }}
                primaryAction={{
                    content: 'Refresh',
                    icon: RefreshIcon,
                    onAction: loadOrders,
                    loading,
                }}
            >
            <Layout>
                <Layout.Section>
                    <BlockStack gap="300">
                        <AtdTabs />

                        {error ? (
                            <Banner tone="critical" title="ATD action failed">
                                <p>{error}</p>
                            </Banner>
                        ) : null}

                        {notice ? (
                            <Banner tone="success" title="ATD action completed">
                                <p>{notice}</p>
                            </Banner>
                        ) : null}

                        <Card padding="0">
                            <IndexFilters
                                sortOptions={sortOptions}
                                sortSelected={[sort]}
                                onSort={handleSortSelectedChange}
                                loading={loading}
                                cancelAction={{ onAction: () => { }, disabled: false, loading: false }}
                                tabs={tabs}
                                selected={selectedTab}
                                onSelect={setSelectedTab}
                                queryValue={query}
                                queryPlaceholder="Search ATD orders"
                                onQueryChange={handleQueryChange}
                                onQueryClear={handleQueryClear}
                                onClearAll={handleFiltersClearAll}
                                filters={filters}
                                appliedFilters={appliedFilters}
                                mode={mode}
                                setMode={setMode}
                            />
                            {loading ? (
                                <Box padding="400">
                                    <Text as="p">Loading ATD orders...</Text>
                                </Box>
                            ) : tableRows.length === 0 ? (
                                <Box padding="400">
                                    <Text as="p" tone="subdued">No ATD fulfillment orders found.</Text>
                                </Box>
                            ) : (
                                <IndexTable
                                    resourceName={{ singular: 'ATD order', plural: 'ATD orders' }}
                                    itemCount={tableRows.length}
                                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                                    onSelectionChange={handleSelectionChange}
                                    promotedBulkActions={promotedBulkActions}
                                    headings={[
                                        { title: 'Order' },
                                        { title: 'Status' },
                                        { title: 'Destination' },
                                        { title: 'Updated' },
                                        { title: 'Actions' },
                                    ]}
                                >
                                    {tableRows.map((order, index) => (
                                        <IndexTable.Row
                                            id={String(order.id)}
                                            key={order.id}
                                            position={index}
                                            selected={allResourcesSelected || selectedResources.includes(String(order.id))}
                                        >
                                            <IndexTable.Cell>
                                                <div style={orderCellStyle}>
                                                    <BlockStack gap="050">
                                                        <button type="button" style={orderLinkButtonStyle} onClick={() => openOrder(order)}>
                                                            <Text as="span" fontWeight="semibold">
                                                                {order.shopifyOrderName || `Order ${order.shopifyOrderId || order.id}`}
                                                            </Text>
                                                        </button>
                                                        <span style={statusBadgeWrapStyle}>
                                                            <Badge tone={isShipToStoreOrder(order) ? 'info' : undefined}>{getFulfillmentTypeLabel(order)}</Badge>
                                                        </span>
                                                        {order.atdOrderNumbers ? (
                                                            <Text as="span" variant="bodySm" tone="subdued">ATD {order.atdOrderNumbers}</Text>
                                                        ) : null}
                                                        {isShipToStoreOrder(order) && order.atdLocationNumber ? (
                                                            <Text as="span" variant="bodySm" tone="subdued">ATD location {order.atdLocationNumber}</Text>
                                                        ) : null}
                                                    </BlockStack>
                                                </div>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <BlockStack gap="100">
                                                    {renderStatusBadge(order)}
                                                </BlockStack>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <div style={{ maxWidth: 240, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                    <Text as="span">{order.shipToName || '-'}</Text>
                                                    {isShipToStoreOrder(order) ? (
                                                        <Text as="p" variant="bodySm" tone="subdued">Customer pickup location</Text>
                                                    ) : null}
                                                    {order.shipToAddressSummary ? (
                                                        <Text as="p" variant="bodySm" tone="subdued">{order.shipToAddressSummary}</Text>
                                                    ) : null}
                                                </div>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>{formatDateTime(order.updatedAt || order.createdAt)}</IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Button variant="plain" onClick={() => openOrder(order)}>View</Button>
                                                    {(order.canRetry || order.canSyncStatus || order.archivedAt || order.canArchive) ? (
                                                        <Popover
                                                            active={openActionOrderId === order.id}
                                                            activator={
                                                                <Button
                                                                    variant="plain"
                                                                    onClick={() => setOpenActionOrderId(current => current === order.id ? undefined : order.id)}
                                                                    disabled={rowActionInProgress}
                                                                    loading={
                                                                        retryingId === order.id ||
                                                                        syncingId === order.id ||
                                                                        archivingId === order.id ||
                                                                        unarchivingId === order.id
                                                                    }
                                                                >
                                                                    More
                                                                </Button>
                                                            }
                                                            onClose={closeOrderActions}
                                                        >
                                                            <ActionList
                                                                items={[
                                                                    ...(order.canRetry ? [{
                                                                        content: 'Refresh preview',
                                                                        onAction: () => {
                                                                            closeOrderActions();
                                                                            handleRetry(order);
                                                                        },
                                                                    }] : []),
                                                                    ...(order.canSyncStatus ? [{
                                                                        content: 'Refresh status',
                                                                        onAction: () => {
                                                                            closeOrderActions();
                                                                            handleSyncOrderStatus(order);
                                                                        },
                                                                    }] : []),
                                                                    ...(order.archivedAt ? [{
                                                                        content: 'Restore',
                                                                        onAction: () => {
                                                                            closeOrderActions();
                                                                            handleUnarchive(order);
                                                                        },
                                                                    }] : order.canArchive ? [{
                                                                        content: 'Archive',
                                                                        onAction: () => {
                                                                            closeOrderActions();
                                                                            handleArchive(order);
                                                                        },
                                                                    }] : []),
                                                                ]}
                                                            />
                                                        </Popover>
                                                    ) : null}
                                                </InlineStack>
                                            </IndexTable.Cell>
                                        </IndexTable.Row>
                                    ))}
                                </IndexTable>
                            )}
                        </Card>

                        <InlineStack align="space-between" blockAlign="center">
                            <Button disabled={page <= 1 || loading} onClick={() => setPage(current => Math.max(1, current - 1))}>Previous</Button>
                            <Text as="span">Page {page} of {totalPages}</Text>
                            <Button disabled={page >= totalPages || loading} onClick={() => setPage(current => current + 1)}>Next</Button>
                        </InlineStack>
                    </BlockStack>
                </Layout.Section>
            </Layout>
            </Page>
        </>
    );
};

export default AtdPage;
